// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SegmentedControlItem.tsx
 * @input Uses React, StyleX, SegmentedControlContext
 * @output Exports SegmentedControlItem component and SegmentedControlItemProps type
 * @position Child item; renders as a radio button within the segmented control
 *
 * SYNC: When modified, update:
 * - /packages/core/src/SegmentedControl/SegmentedControl.doc.mjs
 * - /packages/core/src/SegmentedControl/index.ts
 * - /packages/core/src/SegmentedControl/SegmentedControl.test.tsx
 * - /packages/cli/assets/templates/blocks/components/SegmentedControl/ (showcase blocks)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, sizeVars, durationVars, easeVars, fontWeightVars, shadowVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useSegmentedControlContext } from "./SegmentedControlContext.js";
import { mergeProps, composeEventHandlers } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  base: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "xzye2dw",
    kg3NbH: "xrrkdod",
    kWkggS: "xjbqb8w",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    k63SB2: "x1e4wzip",
    kMwMTN: "xv1l7n4",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x1vix5yk",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  hover: {
    kWkggS: "xe9uy6x",
    $$css: true
  },
  selected: {
    kAXs8y: "x1kzknox",
    kMwMTN: "x1tgivj0 x1k5gbb1",
    k63SB2: "x2mo6ok",
    kWkggS: "x10xzikg x1jzqe4",
    kGVxlE: "x1i5ehqx",
    $$css: true
  },
  disabled: {
    kkrTdU: "xt0e3qv",
    kMwMTN: "xnbbluu",
    $$css: true
  },
  fill: {
    kUk6DE: "x98rzlu",
    kjj79g: "xl56j7k",
    $$css: true
  },
  icon: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    $$css: true
  }
};
const CONCENTRIC_RADIUS = 'max(0px, calc(var(--_segmented-control-radius) - var(--_segmented-control-padding)))';
const sizeStyles = {
  sm: {
    kZKoxP: "xzj98nu",
    kaIpWk: "xc910v0",
    kg3NbH: "xf314gf",
    kGuDYH: "x141an7d",
    $$css: true
  },
  md: {
    kZKoxP: "x184gfjb",
    kaIpWk: "xc910v0",
    kg3NbH: "xrrkdod",
    $$css: true
  },
  lg: {
    kZKoxP: "x1uiybsj",
    kaIpWk: "xc910v0",
    kg3NbH: "xrrkdod",
    $$css: true
  }
};
const iconSizeStyles = {
  sm: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    $$css: true
  },
  md: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    $$css: true
  },
  lg: {
    kzqmXN: "x1xp8n7a",
    kZKoxP: "xmix8c7",
    $$css: true
  }
};

/**
 * Individual segment item within an SegmentedControl.
 * Renders as a radio button with visual segment styling.
 *
 * @example
 * ```
 * <SegmentedControlItem value="grid" label="Grid" icon={<GridIcon />} />
 * ```
 */
export function SegmentedControlItem({
  ref,
  value,
  label,
  isLabelHidden = false,
  icon,
  isDisabled = false,
  onClick: onClickProp,
  xstyle,
  ...rest
}) {
  const ctx = useSegmentedControlContext();
  const isSelected = ctx.value === value;
  const isItemDisabled = isDisabled || ctx.isDisabled;
  // When the whole group is disabled with a disabledMessage, keep the selected
  // segment focusable so the group's reason tooltip is keyboard-discoverable.
  // Per-item disabling (`isDisabled` on the item) always drops out of the tab
  // order. Activation stays blocked by the isItemDisabled guard in handleClick.
  const keepsSelectedFocusable = isSelected && (ctx.hasDisabledMessage ?? false) && !isDisabled;
  const size = ctx.size;
  const isFill = ctx.layout === 'fill';

  // Consumer-first: a consumer onClick can call preventDefault() to opt out of
  // selection; otherwise selection proceeds when enabled and not selected.
  const handleClick = composeEventHandlers(onClickProp, () => {
    if (!isItemDisabled && !isSelected) {
      ctx.onChange(value);
    }
  });
  const iconElement = icon ? /*#__PURE__*/_jsx("span", {
    ...stylex.props(styles.icon, iconSizeStyles[size]),
    children: icon
  }) : null;
  return /*#__PURE__*/_jsxs("button", {
    ref: ref,
    ...rest,
    type: "button",
    role: "radio",
    "aria-checked": isSelected,
    "aria-disabled": isItemDisabled || undefined,
    "aria-label": isLabelHidden ? label : undefined,
    "data-value": value
    // Disabled items (including when the whole group is disabled) are not tab
    // stops — otherwise the selected segment stays keyboard-focusable but is
    // silently dead (arrows and activation are no-ops) (navigation-13). The
    // exception is a whole-group disabledMessage, where the selected segment
    // stays focusable so the reason tooltip is keyboard-discoverable.
    ,
    tabIndex: isSelected && !isItemDisabled || keepsSelectedFocusable ? 0 : -1,
    onClick: handleClick,
    ...mergeProps(themeProps('segmented-control-item', {
      size,
      selected: isSelected ? 'selected' : null,
      disabled: isItemDisabled ? 'disabled' : null
    }), stylex.props(styles.base, sizeStyles[size], isFill && styles.fill, isSelected && styles.selected, !isSelected && !isItemDisabled && styles.hover, isItemDisabled && styles.disabled, xstyle)),
    children: [iconElement, !isLabelHidden && /*#__PURE__*/_jsx("span", {
      children: label
    })]
  });
}
SegmentedControlItem.displayName = 'SegmentedControlItem';